import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Loader2, Settings as SettingsIcon, X, Check, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_LABELS = {
  total: 'Ukupno članaka', published: 'Objavljeno', draft: 'Skice',
  scheduled: 'Zakazano', categories: 'Kategorije', tags: 'Oznake',
  comments_total: 'Komentari', comments_pending: 'Komentari na čekanju',
  users_total: 'Korisnici', jobs_total: 'Aktivni oglasi posla',
  classifieds_total: 'Aktivni oglasi', donations_total: 'Donacije',
  media_total: 'Mediji', breaking_active: 'Breaking članci',
  newsletter_subscribers: 'Newsletter pretplatnici', bug_reports: 'Prijave bugova',
  i18n_coverage_pct: 'Pokrivenost prijevoda (%)',
  ad_inquiries_pending: 'Upiti za oglase',
  news_submissions_total: 'Predane vijesti',
  complaints_total: 'Tuzibaba prijave',
  contact_messages_total: 'Kontakt poruke (ukupno)',
  contact_messages_new: 'Kontakt poruke (nove)',
};

const METRIC_COLORS = {
  total: 'text-blue-400', published: 'text-emerald-400', draft: 'text-amber-400',
  scheduled: 'text-purple-400', categories: 'text-cyan-400', tags: 'text-rose-400',
  comments_total: 'text-indigo-400', comments_pending: 'text-amber-400',
  users_total: 'text-pink-400', jobs_total: 'text-teal-400',
  classifieds_total: 'text-orange-400', donations_total: 'text-emerald-400',
  media_total: 'text-sky-400', breaking_active: 'text-red-400',
  newsletter_subscribers: 'text-violet-400', bug_reports: 'text-yellow-400',
  i18n_coverage_pct: 'text-lime-400',
  ad_inquiries_pending: 'text-fuchsia-400',
  news_submissions_total: 'text-blue-400',
  complaints_total: 'text-amber-400',
  contact_messages_total: 'text-sky-400',
  contact_messages_new: 'text-sky-400',
};

export default function Dashboard() {
  const { authFetch } = useAdmin();
  const [tiles, setTiles] = useState([]);
  const [available, setAvailable] = useState([]);
  const [recent, setRecent] = useState({ articles: [], media: [] });
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [tRes, dRes] = await Promise.all([
      authFetch(`${API}/admin/dashboard-tiles`).then(r => r.json()),
      authFetch(`${API}/admin/dashboard`).then(r => r.ok ? r.json() : { recent_articles: [], recent_media: [] }),
    ]);
    setTiles(tRes.tiles || []);
    setAvailable(tRes.available_metrics || []);
    setRecent({ articles: dRes.recent_articles || [], media: dRes.recent_media || [] });
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleTile = (metric) => {
    setTiles(curr => {
      const idx = curr.findIndex(t => (t.metric || t.key) === metric);
      if (idx >= 0) return curr.filter((_, i) => i !== idx);
      return [...curr, { key: metric, label: METRIC_LABELS[metric] || metric, metric, color: 'blue' }];
    });
  };

  const saveConfig = async () => {
    const r = await authFetch(`${API}/admin/dashboard-tiles`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tiles }),
    });
    if (r.ok) { toast.success('Konfiguracija spremljena'); setConfigOpen(false); load(); }
  };

  const [flushing, setFlushing] = useState(false);
  const flushCache = async () => {
    if (flushing) return;
    setFlushing(true);
    const t = toast.loading('Osvježavam cache i sortiranje članaka…');
    try {
      const r = await authFetch(`${API}/admin/maintenance/flush-cache`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data?.detail || 'Cache flush nije uspio');
      const fixed = data?.normalize?.fixed ?? 0;
      toast.success(
        fixed > 0
          ? `Cache osvježen. Ispravljeno ${fixed} članaka u sortiranju.`
          : 'Cache osvježen. Naslovnica će se ponovno učitati.',
        { id: t },
      );
      // Reload current page so the new cache_version applies immediately.
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error(e.message || 'Greška pri osvježavanju cache-a', { id: t });
    } finally {
      setFlushing(false);
    }
  };

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Nadzorna ploča</h1>
        <div className="flex gap-2">
          <button onClick={() => setConfigOpen(true)} data-testid="configure-tiles-btn"
                  className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md">
            <SettingsIcon size={14} /> Konfiguriraj pločice
          </button>
          <button
            onClick={flushCache}
            disabled={flushing}
            data-testid="flush-cache-btn"
            title="Resetira CDN cache i normalizira sortiranje članaka — koristi ako se nove vijesti ne pojavljuju na naslovnici."
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white text-sm rounded-md transition-colors"
          >
            {flushing
              ? <><Loader2 size={14} className="animate-spin" /> Osvježavam…</>
              : <><RefreshCw size={14} /> Osvježi cache</>}
          </button>
          <Link to="/admin/translate-jobs" className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md" data-testid="dash-mass-translate-btn">
            Masovno prevođenje
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {tiles.map(t => {
          const metricKey = t.metric || t.key;
          const raw = t.value;
          const display = (metricKey === 'i18n_coverage_pct' && raw != null)
            ? `${raw}%`
            : (raw?.toLocaleString?.() ?? raw);
          return (
            <Link key={metricKey} to={LINK_FOR_METRIC[metricKey] || '/admin/articles'}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-600 transition-colors"
                  data-testid={`tile-${metricKey}`}>
              <p className={`text-2xl font-bold ${METRIC_COLORS[metricKey] || 'text-white'}`}>{display}</p>
              <p className="text-xs text-zinc-500 mt-1">{t.label || METRIC_LABELS[metricKey] || metricKey}</p>
            </Link>
          );
        })}
        {tiles.length === 0 && <p className="col-span-full text-center py-4 text-zinc-500 text-sm">Nema pločica. Kliknite "Konfiguriraj" da ih dodate.</p>}
      </div>

      {/* Editorial quick actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        <Link to="/admin/review"
              data-testid="quick-review-link"
              className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 hover:bg-amber-500/20 transition-colors">
          <p className="text-amber-300 font-bold text-sm">⚠️ Članci za pregled</p>
          <p className="text-xs text-amber-100/70 mt-1">Brza obrada članaka sa social bleed-om, praznim tijelom ili razbijenim slikama.</p>
        </Link>
        <Link to="/admin/duplicates"
              data-testid="quick-duplicates-link"
              className="bg-fuchsia-500/10 border border-fuchsia-500/40 rounded-lg p-4 hover:bg-fuchsia-500/20 transition-colors">
          <p className="text-fuchsia-300 font-bold text-sm">📋 Duplikati</p>
          <p className="text-xs text-fuchsia-100/70 mt-1">Otkrivene parove sličnih članaka po naslovu i tijelu.</p>
        </Link>
        <Link to="/admin/premium-promo"
              data-testid="quick-premium-link"
              className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-4 hover:bg-rose-500/20 transition-colors">
          <p className="text-rose-300 font-bold text-sm">👑 Premium promocija</p>
          <p className="text-xs text-rose-100/70 mt-1">Aktiviraj kampanju &quot;Prvih 100 = 1 mjesec premium&quot;.</p>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-zinc-300">Nedavni članci</h2>
            <Link to="/admin/articles" className="text-xs text-blue-400 hover:underline">Prikaži sve</Link>
          </div>
          {recent.articles.length === 0 ? <p className="text-sm text-zinc-500">Nema članaka</p> : (
            <ul className="space-y-2">
              {recent.articles.map(a => (
                <li key={a.id} className="flex items-center justify-between text-sm">
                  <Link to={`/admin/articles/${a.id}/edit`} className="text-zinc-300 hover:text-white truncate mr-2">{a.title_hr}</Link>
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                    a.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' :
                    a.status === 'draft' ? 'bg-amber-500/20 text-amber-400' :
                    a.status === 'scheduled' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>{a.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-zinc-300">Nedavni mediji</h2>
            <Link to="/admin/media" className="text-xs text-blue-400 hover:underline">Prikaži sve</Link>
          </div>
          {recent.media.length === 0 ? <p className="text-sm text-zinc-500">Nema medija</p> : (
            <div className="grid grid-cols-5 gap-2">
              {recent.media.map(m => (
                <div key={m.id} className="aspect-square rounded bg-zinc-800 overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.filename} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {configOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfigOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">Odaberi pločice</h2>
              <button onClick={() => setConfigOpen(false)} className="text-zinc-400"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-400 mb-3">Kliknite metriku da je dodate ili uklonite s nadzorne ploče.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto">
              {available.map(m => {
                const on = tiles.some(t => (t.metric || t.key) === m);
                return (
                  <button key={m} onClick={() => toggleTile(m)} data-testid={`toggle-${m}`}
                          className={`flex items-center justify-between p-2 rounded border text-left text-sm ${
                            on ? 'bg-red-600/20 border-red-500' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-600'
                          }`}>
                    <span>{METRIC_LABELS[m] || m}</span>
                    {on && <Check size={14} className="text-red-400" />}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfigOpen(false)} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-sm">Odustani</button>
              <button onClick={saveConfig} data-testid="save-tiles-btn"
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold">Spremi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const LINK_FOR_METRIC = {
  total: '/admin/articles', published: '/admin/articles', draft: '/admin/articles?status=draft',
  scheduled: '/admin/articles?status=scheduled', categories: '/admin/categories', tags: '/admin/tags',
  comments_total: '/admin/comments', comments_pending: '/admin/comments',
  users_total: '/admin/users', jobs_total: '/admin/jobs',
  classifieds_total: '/admin/classifieds', donations_total: '/admin/tips',
  media_total: '/admin/media', breaking_active: '/admin/breaking-news',
  newsletter_subscribers: '/admin/newsletter', bug_reports: '/admin/bug-reports',
  i18n_coverage_pct: '/admin/i18n-coverage',
  ad_inquiries_pending: '/admin/ad-inquiries',
  news_submissions_total: '/admin/news-submissions',
  complaints_total: '/admin/complaints',
  contact_messages_total: '/admin/contact-messages',
  contact_messages_new: '/admin/contact-messages',
};
